import { useCallback, useEffect, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import type { ProgressPhoto } from "../../types/database";

const BUCKET = "progress-photos";
const SIGNED_URL_TTL_SECONDS = 600;

const todayIso = () => new Date().toISOString().slice(0, 10);

export function usePhotos() {
  const [photos, setPhotos] = useState<ProgressPhoto[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("progress_photos")
      .select("*")
      .order("date", { ascending: false });
    setPhotos(data ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function uploadPhoto(file: File, angle: string, date = todayIso()) {
    const { data: userData } = await supabase.auth.getUser();
    const userId = userData.user!.id;
    const ext = file.name.split(".").pop() ?? "jpg";
    const path = `${userId}/${Date.now()}-${angle}.${ext}`;

    const { error: uploadError } = await supabase.storage.from(BUCKET).upload(path, file, {
      contentType: file.type,
    });
    if (uploadError) throw uploadError;

    const { error: insertError } = await supabase.from("progress_photos").insert({
      user_id: userId,
      date,
      storage_path: path,
      angle,
    });
    if (insertError) throw insertError;
    await load();
  }

  async function signedUrl(path: string): Promise<string | null> {
    const { data } = await supabase.storage.from(BUCKET).createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
    return data?.signedUrl ?? null;
  }

  return { photos, loading, uploadPhoto, signedUrl };
}
